import DOMPurify from 'dompurify';

/**
 * Sanitizes invoice text input to prevent XSS attacks
 * Removes all HTML tags and dangerous content while preserving text
 */
export function sanitizeInvoiceText(text: string): string {
  if (!text) return '';

  // Remove dangerous HTML tags and attributes using DOMPurify
  const clean = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true, // Keep text content
  });

  // Remove javascript: protocol
  const withoutJavaScript = clean.replace(/javascript:/gi, '');

  // Remove data: with script content
  const withoutDataScript = withoutJavaScript.replace(/data:\s*script/gi, '');

  return withoutDataScript;
}

/**
 * Sanitizes file names to prevent path traversal and injection attacks
 */
export function sanitizeFileName(name: string): string {
  if (!name) return '';

  return name
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // Remove invalid filename chars
    .replace(/\.{2,}/g, '.') // Remove consecutive dots (prevent path traversal)
    .replace(/^\.+/, '') // Remove leading dots
    .substring(0, 255); // Limit length
}

/**
 * Sanitizes user-generated content that will be displayed in UI
 * Allows basic formatting but removes scripts and dangerous elements
 */
export function sanitizeUserContent(text: string): string {
  if (!text) return '';

  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
}
